import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import api, { money, formatApiErrorDetail } from "@/dealership/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Receipt, Mail, Pencil, Ban, Printer, Eye, Plus, ChevronDown, ChevronRight,
  BadgeDollarSign, CheckCircle2, ScrollText,
} from "lucide-react";

const METHODS = ["Cash", "Debit", "Credit card", "E-transfer", "Wire", "Bank draft", "Certified cheque", "Cheque", "Financed (lender)", "Other"];
const TYPES = ["Deposit", "Down payment", "Instalment", "Final payment", "Refund"];
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => { if (!d) return ""; try { return new Date((d.length <= 10 ? d + "T00:00:00" : d)).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }); } catch { return d; } };
const fmtDateTime = (d) => { if (!d) return ""; try { return new Date(d).toLocaleString("en-CA", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return d; } };
const apiErr = (e, fallback) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || fallback);

async function printReceiptHtml(paymentId, onDone) {
  try {
    const { data } = await api.get(`/payments/${paymentId}/receipt-html`);
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    iframe.srcdoc = data.html;
    iframe.onload = () => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
      catch (e) { console.error(e); }
      finally { setTimeout(() => iframe.remove(), 1500); }
    };
    document.body.appendChild(iframe);
    try { await api.post(`/payments/${paymentId}/mark-printed`); } catch { /* non-blocking */ }
    onDone && onDone();
  } catch (e) { apiErr(e, "Could not open the receipt for printing"); }
}

export default function DealPayments({ deal, onChanged }) {
  const bal = deal.balance || {};
  const payments = bal.payments || [];
  const dealId = deal.id;

  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Cash");
  const [ptype, setPtype] = useState("Instalment");
  const [pdate, setPdate] = useState(today());
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");

  const [justCreated, setJustCreated] = useState(null);
  const [editing, setEditing] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [emailing, setEmailing] = useState(null);
  const [viewing, setViewing] = useState(null); // {html}
  const [funding, setFunding] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  // Pre-fill amount with the remaining balance; refreshes when the balance changes.
  useEffect(() => { setAmount(bal.balance_due > 0 ? String(bal.balance_due) : ""); }, [bal.balance_due]);

  const recordMut = useMutation({
    mutationFn: async () => (await api.post(`/deals/${dealId}/payments`, {
      amount: Number(amount), method, payment_type: ptype, payment_date: pdate,
      reference: reference.trim(), notes: notes.trim(),
    })).data,
    onSuccess: (data) => {
      setJustCreated(data.payment);
      setReference(""); setNotes(""); setPtype("Instalment"); setPdate(today());
      toast.success(`Payment recorded · ${data.payment.receipt_number}`);
      onChanged && onChanged(data.balance);
    },
    onError: (e) => apiErr(e, "Failed to record payment"),
  });

  const submitRecord = () => {
    if (!Number(amount) || Number(amount) <= 0) { toast.error("Enter a payment amount greater than zero"); return; }
    recordMut.mutate();
  };

  const balanceLabel = () => {
    if (bal.overpaid) return <span className="text-[#16A34A]" data-testid="payment-overpaid">Credit / overpaid {money(Math.abs(bal.balance_due))}</span>;
    if (bal.paid_in_full) return <span className="text-[#16A34A]" data-testid="payment-paid-in-full">PAID IN FULL</span>;
    return <span className="text-primary" data-testid="payment-balance-due">{money(bal.balance_due)}</span>;
  };

  const statusPill = bal.lender_funding_status && bal.lender_funding_status !== "Not financed"
    ? bal.lender_funding_status : "";
  const showFunding = (bal.amount_financed || 0) > 0 || statusPill;

  return (
    <div className="space-y-6" data-testid="payments-section">
      {/* ---------- Balance summary ---------- */}
      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-border p-5 bg-secondary/30" data-testid="payment-summary">
          <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-3">Balance</p>
          <SumRow l="Deal total" v={money(bal.deal_total)} />
          <SumRow l={bal.hst ? "HST (included above)" : "HST"} v={bal.hst ? money(bal.hst) : "—"} muted />
          {bal.trade_in_value > 0 && <SumRow l="Trade-in allowance (informational)" v={money(bal.trade_in_value)} muted />}
          {bal.lien_amount > 0 && <SumRow l="Lien on trade (informational)" v={money(bal.lien_amount)} muted />}
          {showFunding && (
            <div className="flex items-center justify-between py-1.5 text-sm" data-testid="funding-status-line">
              <span className="text-muted-foreground flex items-center gap-2">
                Amount financed
                <Badge className={`border-0 text-[10px] ${bal.lender_funding_status === "Funded" ? "bg-green-500/15 text-green-400" : bal.lender_funding_status === "Pending" ? "bg-amber-500/15 text-amber-400" : "bg-zinc-500/15 text-zinc-400"}`}>
                  {bal.lender_funding_status}
                </Badge>
                {bal.lender_funding_status !== "Funded" && (bal.amount_financed || 0) > 0 && (
                  <span className="text-[11px] text-muted-foreground">pending credit — not deducted</span>
                )}
              </span>
              <span className={bal.lender_funding_status === "Funded" ? "font-semibold" : "text-muted-foreground"}>
                {bal.lender_funding_status === "Funded" ? `- ${money(bal.amount_financed)}` : money(bal.amount_financed)}
              </span>
            </div>
          )}
          {bal.lender_funding_status === "Funded" && bal.funded_date && (
            <p className="text-[11px] text-muted-foreground -mt-1 mb-1">Funded {fmtDate(bal.funded_date)}</p>
          )}
          <SumRow l="Total paid to date" v={`- ${money(bal.total_paid)}`} bold />
          <div className="border-t border-border mt-2 pt-2 flex items-center justify-between">
            <span className="text-sm font-semibold">Balance due</span>
            <span className="text-2xl font-bold font-mono-data">{balanceLabel()}</span>
          </div>
          {bal.lender_funding_status !== "Funded" && (bal.amount_financed || 0) > 0 && (
            <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => setFunding(true)} data-testid="mark-funded-button">
              <BadgeDollarSign className="h-3.5 w-3.5 mr-1.5" />Mark lender as funded
            </Button>
          )}
        </div>

        {/* ---------- Record a payment ---------- */}
        <div className="rounded-2xl border border-border p-5" data-testid="record-payment-form">
          <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-3">Record a payment</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Amount</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="record-payment-amount" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" value={pdate} onChange={(e) => setPdate(e.target.value)} data-testid="record-payment-date" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Type</Label>
              <Select value={ptype} onValueChange={setPtype}><SelectTrigger data-testid="record-payment-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Method</Label>
              <Select value={method} onValueChange={setMethod}><SelectTrigger data-testid="record-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5 col-span-2"><Label className="text-xs">Reference <span className="text-muted-foreground">(cheque #, auth code…)</span></Label><Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="record-payment-reference" /></div>
            <div className="space-y-1.5 col-span-2"><Label className="text-xs">Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="record-payment-notes" /></div>
          </div>
          <Button className="mt-3 w-full" onClick={submitRecord} disabled={recordMut.isPending} data-testid="record-payment-submit">
            <Plus className="h-4 w-4 mr-1.5" />{recordMut.isPending ? "Recording…" : "Record payment"}
          </Button>

          {justCreated && (
            <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="just-created-banner">
              <span className="text-sm">Receipt <span className="font-semibold">{justCreated.receipt_number}</span> ready</span>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => openView(justCreated.id, setViewing)} data-testid="just-created-view"><Eye className="h-3.5 w-3.5 mr-1" />View</Button>
                <Button size="sm" variant="ghost" onClick={() => printReceiptHtml(justCreated.id, onChanged)} data-testid="just-created-print"><Printer className="h-3.5 w-3.5 mr-1" />Print</Button>
                <Button size="sm" variant="outline" onClick={() => setEmailing(justCreated)} data-testid="just-created-email"><Mail className="h-3.5 w-3.5 mr-1" />Email</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------- Payment history ---------- */}
      <div className="rounded-2xl border border-border overflow-hidden" data-testid="payment-history">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Payment history</p>
        </div>
        {payments.length === 0 ? (
          <p className="text-sm text-muted-foreground italic px-5 py-6">No payments recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground border-b border-border">
                  <th className="text-left px-3 py-2">Receipt #</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Method</th>
                  <th className="text-left px-3 py-2">Reference</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Received by</th>
                  <th className="text-center px-3 py-2">Sent</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const stale = p.emailed_at && p.last_edited_at && p.last_edited_at > p.emailed_at;
                  const revised = (p.revision || 1) > 1;
                  return (
                    <tr key={p.id} className={`border-b border-border/60 last:border-0 ${p.voided ? "opacity-50 line-through" : ""}`} data-testid={`payment-row-${p.id}`}>
                      <td className="px-3 py-2 font-mono-data">
                        <span className="inline-flex items-center gap-1.5">
                          {p.receipt_number}
                          {revised && (
                            <span title={`Revised ${fmtDate(p.last_edited_at?.slice(0, 10))}${p.last_edited_by ? ` by ${p.last_edited_by}` : ""}`}
                              className="no-underline text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400" data-testid={`payment-revised-chip-${p.id}`}>Revised</span>
                          )}
                          {p.voided && <span title={p.void_reason || "Voided"} className="no-underline text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400">Void</span>}
                        </span>
                      </td>
                      <td className="px-3 py-2">{fmtDate(p.payment_date)}</td>
                      <td className="px-3 py-2">{p.payment_type}</td>
                      <td className="px-3 py-2">{p.method}</td>
                      <td className="px-3 py-2 text-muted-foreground">{p.reference || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-data font-semibold">{p.payment_type === "Refund" ? `- ${money(p.amount)}` : money(p.amount)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{p.received_by || "—"}</td>
                      <td className="px-3 py-2 text-center">
                        {p.emailed_at ? (
                          <span title={`Emailed to ${p.emailed_to || "recipient"} on ${fmtDateTime(p.emailed_at)}${stale ? " — edited since; customer may hold an outdated copy" : ""}`} data-testid={`payment-sent-icon-${p.id}`}>
                            <Mail className={`h-4 w-4 inline ${stale ? "text-amber-400" : "text-emerald-400"}`} />
                          </span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-0.5 no-underline">
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="View receipt" onClick={() => openView(p.id, setViewing)} data-testid={`view-payment-${p.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Print receipt" onClick={() => printReceiptHtml(p.id, onChanged)} data-testid={`print-payment-${p.id}`}><Printer className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Email receipt" onClick={() => setEmailing(p)} data-testid={`email-payment-${p.id}`}><Mail className="h-3.5 w-3.5 text-emerald-400" /></Button>
                          {!p.voided && <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit payment" onClick={() => setEditing({ ...p, reason: "" })} data-testid={`edit-payment-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                          {!p.voided && <Button size="icon" variant="ghost" className="h-7 w-7" title="Void payment" onClick={() => setVoiding({ ...p, reason: "" })} data-testid={`void-payment-${p.id}`}><Ban className="h-3.5 w-3.5 text-destructive" /></Button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-border font-semibold">
                  <td className="px-3 py-2" colSpan={5}>Total paid (excludes voided)</td>
                  <td className="px-3 py-2 text-right font-mono-data" data-testid="payment-total-paid">{money(bal.total_paid)}</td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ---------- Audit log (admin) ---------- */}
      <AuditLog dealId={dealId} payments={payments} show={showAudit} onToggle={() => setShowAudit((s) => !s)} />

      {/* ---------- Dialogs ---------- */}
      <EditDialog deal={deal} payment={editing} onClose={() => setEditing(null)} onChanged={onChanged} />
      <VoidDialog payment={voiding} onClose={() => setVoiding(null)} onChanged={onChanged} />
      <EmailDialog deal={deal} payment={emailing} onClose={() => setEmailing(null)} onChanged={onChanged} />
      <ViewDialog state={viewing} onClose={() => setViewing(null)} />
      <FundingDialog open={funding} deal={deal} onClose={() => setFunding(false)} onChanged={onChanged} />
    </div>
  );
}

function SumRow({ l, v, bold, muted }) {
  return (
    <div className={`flex justify-between py-1.5 text-sm ${muted ? "text-muted-foreground" : ""}`}>
      <span className={muted ? "" : "text-muted-foreground"}>{l}</span>
      <span className={`font-mono-data ${bold ? "font-semibold" : ""}`}>{v}</span>
    </div>
  );
}

async function openView(paymentId, setViewing) {
  try {
    const { data } = await api.get(`/payments/${paymentId}/receipt-html`);
    setViewing({ html: data.html });
  } catch (e) { apiErr(e, "Could not open the receipt"); }
}

function EditDialog({ deal, payment, onClose, onChanged }) {
  const [f, setF] = useState(null);
  useEffect(() => { setF(payment ? { ...payment } : null); }, [payment]);
  const mut = useMutation({
    mutationFn: async () => (await api.patch(`/payments/${payment.id}`, {
      amount: Number(f.amount), method: f.method, payment_type: f.payment_type,
      payment_date: f.payment_date, reference: f.reference || "", notes: f.notes || "",
      reason: (f.reason || "").trim(),
    })).data,
    onSuccess: (data) => { toast.success("Payment updated"); onChanged && onChanged(data.balance); onClose(); },
    onError: (e) => apiErr(e, "Failed to update payment"),
  });
  if (!payment || !f) return null;
  const amountChanged = Number(f.amount) !== Number(payment.amount);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-payment-dialog">
        <DialogHeader><DialogTitle>Edit payment · {payment.receipt_number}</DialogTitle>
          <DialogDescription className="sr-only">Edit this payment's details.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Amount</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="edit-payment-amount" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Type</Label>
            <Select value={f.payment_type} onValueChange={(v) => setF({ ...f, payment_type: v })}><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label className="text-xs">Method</Label>
            <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5 col-span-2"><Label className="text-xs">Reference</Label><Input value={f.reference || ""} onChange={(e) => setF({ ...f, reference: e.target.value })} /></div>
          <div className="space-y-1.5 col-span-2"><Label className="text-xs">Notes</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          {amountChanged && (
            <div className="space-y-1.5 col-span-2"><Label className="text-xs text-amber-500">Reason for changing the amount (required)</Label>
              <Input value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="edit-reason-input" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || (amountChanged && !(f.reason || "").trim())} data-testid="edit-payment-save">
            {mut.isPending ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VoidDialog({ payment, onClose, onChanged }) {
  const [reason, setReason] = useState("");
  useEffect(() => { setReason(""); }, [payment]);
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/payments/${payment.id}/void`, { reason: reason.trim() })).data,
    onSuccess: (data) => { toast.success("Payment voided"); onChanged && onChanged(data.balance); onClose(); },
    onError: (e) => apiErr(e, "Failed to void payment"),
  });
  if (!payment) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="void-payment-dialog">
        <DialogHeader><DialogTitle>Void payment · {payment.receipt_number}</DialogTitle>
          <DialogDescription>Voiding keeps the record for the audit trail but removes {money(payment.amount)} from the balance.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label className="text-xs">Reason</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this payment being voided?" data-testid="void-reason-input" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="void-payment-confirm">{mut.isPending ? "Voiding…" : "Void payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailDialog({ deal, payment, onClose, onChanged }) {
  const [to, setTo] = useState("");
  useEffect(() => { setTo(deal.customer_email || ""); }, [payment, deal.customer_email]);
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/payments/${payment.id}/email`, { to: to.trim() })).data,
    onSuccess: (data) => { toast.success(data.warning ? data.warning : `Receipt emailed to ${data.to}`); onChanged && onChanged(); onClose(); },
    onError: (e) => apiErr(e, "Failed to email the receipt"),
  });
  if (!payment) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="email-payment-dialog">
        <DialogHeader><DialogTitle>Email receipt · {payment.receipt_number}</DialogTitle>
          <DialogDescription>Sends the receipt as an HTML email with a PDF attached, via the dealership's connected Gmail.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label className="text-xs">Send to</Label><Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@email.com" data-testid="email-to-input" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !to.trim()} data-testid="email-send-confirm"><Mail className="h-4 w-4 mr-1.5" />{mut.isPending ? "Sending…" : "Send receipt"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewDialog({ state, onClose }) {
  if (!state) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden p-0" data-testid="view-receipt-dialog">
        <DialogHeader className="px-5 pt-5"><DialogTitle>Receipt preview</DialogTitle>
          <DialogDescription className="sr-only">A preview of the receipt document.</DialogDescription></DialogHeader>
        <iframe title="Receipt preview" srcDoc={state.html} className="w-full h-[70vh] bg-white border-0" />
      </DialogContent>
    </Dialog>
  );
}

function FundingDialog({ open, deal, onClose, onChanged }) {
  const [date, setDate] = useState(today());
  useEffect(() => { if (open) setDate(deal.funded_date || today()); }, [open, deal.funded_date]);
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/deals/${deal.id}/funding`, { status: "Funded", funded_date: date })).data,
    onSuccess: (data) => { toast.success("Deal marked as funded"); onChanged && onChanged(data.balance); onClose(); },
    onError: (e) => apiErr(e, "Failed to update funding status"),
  });
  if (!open) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="mark-funded-dialog">
        <DialogHeader><DialogTitle>Mark lender as funded</DialogTitle>
          <DialogDescription>The amount financed ({money(deal.balance?.amount_financed)}) will be credited to the balance. This can close the deal automatically.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label className="text-xs">Funded date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="funded-date-input" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="mark-funded-confirm"><CheckCircle2 className="h-4 w-4 mr-1.5" />{mut.isPending ? "Saving…" : "Mark as funded"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ACTION_META = {
  created: { label: "Recorded", cls: "bg-green-500/15 text-green-400" },
  refunded: { label: "Refund", cls: "bg-amber-500/15 text-amber-400" },
  edited: { label: "Edited", cls: "bg-blue-500/15 text-blue-400" },
  voided: { label: "Voided", cls: "bg-red-500/15 text-red-400" },
  printed: { label: "Printed", cls: "bg-zinc-500/15 text-zinc-400" },
  emailed: { label: "Emailed", cls: "bg-indigo-500/15 text-indigo-400" },
  funded: { label: "Funded", cls: "bg-[hsl(var(--gold)/0.15)] text-primary" },
  funding_changed: { label: "Funding", cls: "bg-[hsl(var(--gold)/0.15)] text-primary" },
};

function AuditLog({ dealId, payments, show, onToggle }) {
  const { data: log = [], isFetching } = useQuery({
    queryKey: ["deal-payment-audit", dealId],
    queryFn: async () => (await api.get(`/deals/${dealId}/payment-audit`)).data,
    enabled: show,
  });
  const rcpByPayment = useMemo(() => {
    const m = {};
    (payments || []).forEach((p) => { m[p.id] = p.receipt_number; });
    return m;
  }, [payments]);

  return (
    <div className="rounded-2xl border border-border overflow-hidden" data-testid="audit-log-card">
      <button onClick={onToggle} className="w-full px-5 py-3 flex items-center gap-2 hover:bg-secondary/40 transition-colors" data-testid="audit-log-toggle">
        {show ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <ScrollText className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Payment history log (audit)</span>
      </button>
      {show && (
        <div className="px-5 pb-4 pt-1" data-testid="audit-log">
          {isFetching ? <p className="text-sm text-muted-foreground py-3">Loading…</p>
            : log.length === 0 ? <p className="text-sm text-muted-foreground py-3">No activity yet.</p>
              : (
                <ol className="space-y-2">
                  {log.map((e) => {
                    const meta = ACTION_META[e.action] || { label: e.action, cls: "bg-zinc-500/15 text-zinc-400" };
                    const rcp = e.payment_id ? rcpByPayment[e.payment_id] : "";
                    const diff = e.before && e.after
                      ? Object.keys(e.after).filter((k) => String(e.before[k] ?? "") !== String(e.after[k] ?? ""))
                      : [];
                    return (
                      <li key={e.id} className="text-sm border-l-2 border-border pl-3 py-1" data-testid={`audit-entry-${e.id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`border-0 text-[10px] ${meta.cls}`}>{meta.label}</Badge>
                          {rcp ? <span className="font-mono-data text-xs">{rcp}</span> : <span className="text-xs text-muted-foreground">Deal</span>}
                          <span className="text-xs text-muted-foreground">· {e.user}</span>
                          <span className="text-xs text-muted-foreground ml-auto">{fmtDateTime(e.at)}</span>
                        </div>
                        {e.action === "created" && (
                          <p className="text-xs text-muted-foreground mt-0.5">{e.payment_type} · {e.method} · {money(e.amount)}</p>
                        )}
                        {e.action === "voided" && e.reason && <p className="text-xs text-muted-foreground mt-0.5">Reason: {e.reason}</p>}
                        {e.action === "emailed" && <p className="text-xs text-muted-foreground mt-0.5">Sent to {e.to}{e.attachment === false ? " (no PDF attached)" : ""}</p>}
                        {diff.length > 0 && (
                          <ul className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                            {diff.map((k) => (
                              <li key={k}><span className="text-foreground/70">{k.replace(/_/g, " ")}:</span> <span className="line-through">{fmtVal(k, e.before[k])}</span> → {fmtVal(k, e.after[k])}</li>
                            ))}
                            {e.reason && <li className="text-amber-500/80">Reason: {e.reason}</li>}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
        </div>
      )}
    </div>
  );
}

function fmtVal(key, v) {
  if (v === "" || v == null) return "—";
  if (key === "amount") return money(v);
  return String(v);
}
